// `import type`: o type-stripping do Node apaga a linha inteira ao rodar o teste com
// `node --test` — um import de valor tentaria resolver 'types' em runtime e quebraria.
import type { Negociacao } from '../../../types';

/**
 * Lógica pura do tempo real do board. Fica fora do componente por dois motivos:
 * é o pedaço que dá para testar sem React (ver __tests__/realtimeNegociacoes.test.ts)
 * e é onde moram as regras que, erradas, apagam dado da tela sem ninguém perceber.
 */

/** Campos que só o board calcula (selo Lemit). A linha recarregada do banco NÃO os traz —
 *  sobrescrever a negociação inteira com o que veio do PostgREST apagaria o selo do card
 *  até a próxima carga cheia. Por isso o merge é campo a campo, preservando estes. */
const CAMPOS_SO_DO_CLIENTE = ['_lemitEnriquecido', '_lemitDono'] as const;

/** Extrai o id da negociação de um evento de postgres_changes.
 *  DELETE só traz a PK em `old` (replica identity default) — daí o fallback.
 *  Devolve `null` quando o payload não tem id utilizável: acontece quando o Realtime
 *  trunca o registro por exceder o limite de payload (a linha de `negociacoes` carrega
 *  `documentos_json`, que pode ser grande). Quem chama trata isso como "recarrega tudo",
 *  nunca como "nada aconteceu" — senão a mudança some da tela em silêncio. */
export const idDoEvento = (payload: { new?: any; old?: any; errors?: any }): number | null => {
    const bruto = payload?.new?.id ?? payload?.old?.id;
    const id = Number(bruto);
    return Number.isFinite(id) && bruto != null ? id : null;
};

/** Mescla a linha nova por cima da que já está na tela, preservando os campos locais. */
export const mesclarLinha = (anterior: Negociacao | undefined, nova: Negociacao): Negociacao => {
    if (!anterior) return nova;
    const saida: any = { ...nova };
    for (const campo of CAMPOS_SO_DO_CLIENTE) {
        // `in` e não truthiness: `false` é valor legítimo (já consultado, sem enriquecimento).
        if (campo in (anterior as any) && !(campo in saida)) saida[campo] = (anterior as any)[campo];
    }
    return saida as Negociacao;
};

/**
 * Aplica um lote de mudanças na lista do board.
 *
 * `idsPedidos` são os ids que o evento apontou; `encontradas` é o que a query voltou
 * **com o mesmo recorte da carga cheia** (funil + papel + filtros de servidor). A
 * assimetria entre os dois é a informação: id pedido que não voltou é linha que saiu
 * da vista — foi apagada, mudou de funil, trocou de responsável, virou "perdido" com o
 * toggle desligado, ou o RLS não deixa mais o usuário vê-la. Em todos esses casos o
 * card tem que sumir, exatamente como sumiria num F5.
 *
 * Linha nova entra no fim: a ordem de exibição é decidida no cliente (`sortOption`),
 * então a posição no array não importa.
 */
export const aplicarPatch = (
    lista: Negociacao[],
    encontradas: Negociacao[],
    idsPedidos: number[],
): Negociacao[] => {
    if (idsPedidos.length === 0) return lista;

    const porId = new Map<number, Negociacao>();
    for (const n of encontradas) porId.set(Number(n.id), n);
    const pedidos = new Set(idsPedidos.map(Number));

    const saida: Negociacao[] = [];
    const jaNaLista = new Set<number>();
    let mudou = false;

    for (const atual of lista) {
        const id = Number(atual.id);
        if (!pedidos.has(id)) { saida.push(atual); continue; }
        jaNaLista.add(id);
        const nova = porId.get(id);
        if (!nova) { mudou = true; continue; } // saiu da vista
        saida.push(mesclarLinha(atual, nova));
        mudou = true;
    }

    for (const id of pedidos) {
        if (jaNaLista.has(id)) continue;
        const nova = porId.get(id);
        if (nova) { saida.push(nova); mudou = true; }
    }

    // Identidade preservada quando nada mudou: evita re-render e, principalmente, evita
    // reexecutar os useMemo de filtro/ordenação do board a cada evento irrelevante.
    return mudou ? saida : lista;
};

/**
 * Descarte barato: dá para ignorar este evento sem nem consultar o banco?
 *
 * Só quando as duas coisas valem: a linha **não** está na tela (uma que está pode
 * precisar sumir — mudou de funil, de responsável, foi apagada) e o `pipeline_id` novo
 * é outro funil, não-nulo (o board também mostra `pipeline_id IS NULL`). Nesse caso o
 * evento só poderia significar "entrar no quadro", e entrar se decide pelos valores
 * novos, que estão aqui.
 *
 * Nunca descarta por falta de informação: DELETE (sem `new`) e payload truncado passam.
 * E de propósito NÃO olha `old.pipeline_id`: com REPLICA IDENTITY DEFAULT o `old` de um
 * UPDATE traz só a PK, então "de qual funil saiu" é uma pergunta sem resposta aqui.
 */
export const ehDeOutroFunil = (
    payload: { new?: any },
    funilAtual: string | null,
    idsNaTela: Set<number>,
): boolean => {
    const id = Number(payload?.new?.id);
    if (Number.isFinite(id) && idsNaTela.has(id)) return false;
    const funilNovo = payload?.new?.pipeline_id;
    if (funilNovo === undefined || funilNovo === null) return false;
    return String(funilNovo) !== String(funilAtual);
};
